import React, { useCallback, useEffect, useMemo, useState } from 'react';
import type { Session } from '@supabase/supabase-js';
import { supabase } from '../supabaseClient';
import type { AuthContextValue, Capabilities } from './authTypes';
import { AuthContext } from './AuthContext';
import { captureAppError, captureDistributionMetric, setSentryUser } from '../sentry';

const emptyCaps: Capabilities = {
  isAdmin: false,
  canManageEvents: false,
  canManageCostumes: false,
};

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [session, setSession] = useState<Session | null>(null);
  const [loading, setLoading] = useState(true);
  const [capabilities, setCapabilities] = useState<Capabilities>(emptyCaps);

  const refreshCapabilitiesFor = useCallback(async (sess: Session | null) => {
    if (!sess?.user?.id) {
      setCapabilities(emptyCaps);
      return;
    }

    const startedAt = performance.now();
    try {
      const [isAdminRes, canManageEventsRes, canManageCostumesRes] = await Promise.all([
        supabase.rpc('is_admin'),
        supabase.rpc('can_manage_events'),
        supabase.rpc('can_manage_costumes'),
      ]);
      if (isAdminRes.error) throw isAdminRes.error;
      if (canManageEventsRes.error) throw canManageEventsRes.error;
      if (canManageCostumesRes.error) throw canManageCostumesRes.error;

      setCapabilities({
        isAdmin: isAdminRes.data === true,
        canManageEvents: canManageEventsRes.data === true,
        canManageCostumes: canManageCostumesRes.data === true,
      });
      captureDistributionMetric(
        'admin_web.auth.refresh_capabilities_duration',
        performance.now() - startedAt,
        {
          success: true,
        },
      );
    } catch (error) {
      captureDistributionMetric(
        'admin_web.auth.refresh_capabilities_duration',
        performance.now() - startedAt,
        {
          success: false,
        },
      );
      captureAppError(error, 'auth.refreshCapabilities');
      setCapabilities(emptyCaps);
    }
  }, []);

  const refreshCapabilities = useCallback(async () => {
    await refreshCapabilitiesFor(session);
  }, [refreshCapabilitiesFor, session]);

  useEffect(() => {
    setSentryUser(
      session?.user?.id
        ? {
            id: session.user.id,
            role: capabilities.isAdmin ? 'admin' : 'member',
            tenant: 'mkapp',
          }
        : undefined,
    );
  }, [capabilities.isAdmin, session?.user?.id]);

  useEffect(() => {
    let mounted = true;

    (async () => {
      const startedAt = performance.now();
      try {
        const { data } = await supabase.auth.getSession();
        if (!mounted) return;
        setSession(data.session);
        setLoading(false);
        captureDistributionMetric(
          'admin_web.auth.get_session_duration',
          performance.now() - startedAt,
          {
            success: true,
          },
        );
        void refreshCapabilitiesFor(data.session);
      } catch (error) {
        captureDistributionMetric(
          'admin_web.auth.get_session_duration',
          performance.now() - startedAt,
          {
            success: false,
          },
        );
        captureAppError(error, 'auth.getSession');
        if (!mounted) return;
        setSession(null);
        setLoading(false);
      }
    })();

    const { data: sub } = supabase.auth.onAuthStateChange((_event, newSession) => {
      setSession(newSession);
      void refreshCapabilitiesFor(newSession);
    });

    return () => {
      mounted = false;
      sub.subscription.unsubscribe();
    };
  }, []);

  const signOut = useCallback(async () => {
    // Local-only signout keeps other device sessions (e.g. mobile app) active.
    await supabase.auth.signOut({ scope: 'local' });
    setSentryUser();
    try {
      localStorage.removeItem('mk-admin-locked');
    } catch {
      // ignore localStorage cleanup errors
    }
    setCapabilities(emptyCaps);
  }, []);

  const value = useMemo<AuthContextValue>(
    () => ({
      session,
      loading,
      capabilities,
      refreshCapabilities,
      signOut,
    }),
    [capabilities, loading, refreshCapabilities, session, signOut],
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}
